<template>
  <div class="h-100 w-100" :class="{ 'p-3': !hasFiles }">
    <input
      type="file"
      class="d-none"
      ref="fileInput"
      :id="fileInputId"
      :accept="acceptFiles"
      @change="onChangeFileInput"
      multiple
    />
    <label
      v-show="!hasFiles"
      :for="fileInputId"
      class="drop-target w-100 cursor-pointer border rounded h-100 p-2"
      :style="{ 'background-color': bgColor }"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <div class="drop-target-add-files h-100 rounded">
        <slot name="label">
          <div class="d-flex align-items-center justify-content-center">
            <div class="h1 pr-3">
              <i class="text-dark-50 flaticon-upload-1"></i>
            </div>
            <div>Drop files here or browse files</div>
          </div>
        </slot>
      </div>
    </label>

    <div
      v-if="title"
      v-show="hasFiles"
      class="kt-portlet__head align-items-center"
    >
      <div class="kt-portlet__head-label">
        <h3 class="kt-portlet__head-title">
          {{ title }}
        </h3>
      </div>
      <label
        v-if="canUploadFiles"
        :for="fileInputId"
        class="cursor-pointer"
        style="line-height: 0;"
      >
        <i class="text-dark-50 flaticon2-plus"></i> Add files
      </label>
    </div>

    <!-- <div v-if="failedFiles.length > 0" class="failed-files-actions mb-3">
      <button
        type="button"
        @click="retryAllFailed"
        class="btn btn-warning btn-sm"
        :disabled="isRetryingAll"
      >
        <i class="flaticon2-refresh"></i> Retry All Failed ({{ failedFiles.length }})
      </button>
    </div> -->

    <div
      v-show="hasFiles"
      :id="uppyDashboardUiId"
      :style="{ height: title ? dashboardHeight : '100% !important' }"
    ></div>

    <div
      v-if="hasFiles"
      class="upload-metrics mb-3 mt-2"
    >
      <div
        v-if="rateLimitRemainingSeconds != null"
        class="rate-limit-countdown mb-2 p-2 rounded"
      >
        <span>
          <span aria-hidden="true">⚠️</span>
          Upload rate limited. Retrying in <strong>{{ rateLimitRemainingSeconds }}s</strong>…
        </span>
      </div>

      <div class="d-flex justify-content-start align-items-center flex-wrap gap-2">
        <div>
          <span class="text-muted">Progress:</span>
          <strong class="ml-2">{{ overallProgress }}%</strong>
        </div>
        <template v-if="overallProgress > 0 && overallProgress < 100">
          <div>
            <span class="text-muted ml-2">Speed:</span>
            <strong class="ml-2">{{ uploadMetrics.speed }}</strong>
          </div>
          <div>
            <span class="text-muted ml-2">ETA:</span>
            <strong class="ml-2">{{ uploadMetrics.eta }}</strong>
          </div>
        </template>
        <div v-else-if="overallProgress === 0 && hasFiles && rateLimitRemainingSeconds == null" class="text-muted ml-3">
          Queued / starting…
        </div>
        <div v-else-if="overallProgress === 100" class="badge badge-pill badge-success ml-3">
          Completed
        </div>
        <div
          v-if="showFileCountHint"
          class=" ml-2 p-2 text-muted "
        >
          <strong class="fw-bold">{{ fileCountHintMessage }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import AwsS3 from "@uppy/aws-s3";
import { v4 as uuidv4 } from "uuid";
import { mapGetters } from "vuex";
import { UPLOAD_CONFIG } from "@/config/uploadConfig";
import { globalNetworkMonitor } from "@/helpers/networkMonitor";
import { parseRetryAfter } from "@/helpers/uppyRetryHandler";
import { UploadProgressTracker } from "@/helpers/uploadProgressTracker";
import {
  generatePresignedUrl,
  confirmUpload
} from "@/services/cdnUploadService";
import {
  initiateMultipartUpload,
  getPresignedPartUrl,
  completeMultipartUpload as completeMultipartUploadService,
  abortMultipartUpload
} from "@/services/multipartUploadService";

export default {
  props: {
    title: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      required: true,
    },
    maxFiles: {
      type: Number,
      default: null,
    },
    fileTypes: {
      type: Array,
      default: null,
    },
    meta: {
      type: Object,
      default: () => {
        return {};
      },
    },
    headers: {
      type: Object,
      default: () => {
        return {};
      },
    },
    bgColor: {
      type: String,
      default: "#FFFCF1",
    },
    dashboardHeight: {
      type: String,
      default: "100% !important",
    },
  },
  data() {
    return {
      uppy: null,
      failedFiles: [],
      overallProgress: 0,
      isDestroyingComponent: false,
      networkMonitor: null,
      progressTracker: null,
      uploadMetrics: {
        speed: "0 B/s",
        eta: "calculating...",
      },
      isRetryingAll: false,
      rateLimitRetryTimeouts: {},
      rateLimitRemainingSeconds: null,
      rateLimitCountdownIntervalId: null,
    };
  },
  computed: {
    ...mapGetters({
      images: "order/images",
    }),
    uppyId() {
      return uuidv4();
    },
    fileInputId() {
      return `upload-Files-${this.uppyId}`;
    },
    uppyDashboardUiId() {
      return `uppy-dashboard-ui-${this.uppyId}`;
    },
    acceptFiles() {
      if (!this.fileTypes || this.fileTypes.length === 0) {
        return null;
      }
      return this.fileTypes.toString();
    },
    hasFiles() {
      if (!this.uppy) {
        return false;
      }
      return !!this.uppy.getFiles().length;
    },
    canUploadFiles() {
      if (!this.maxFiles || !this.hasFiles) {
        return true;
      }
      return this.uppy.getFiles().length < this.maxFiles;
    },
    totalFileCount() {
      return this.uppy ? this.uppy.getFiles().length : 0;
    },
    showScrollHint() {
      return this.totalFileCount > UPLOAD_CONFIG.SCROLL_HINT_THRESHOLD;
    },
    fileCountHintMessage() {
      if (!this.hasFiles) return "";

      const total = this.totalFileCount;
      const failed = this.failedFiles.length;
      const successful = total - failed;

      if (this.overallProgress === 100) {
        if (failed === 0) {
          return `Uploaded ${total} file${total !== 1 ? "s" : ""} successfully.`;
        }
        return `Uploaded ${successful} file${successful !== 1 ? "s" : ""}, failed ${failed} file${failed !== 1 ? "s" : ""}.`;
      }

      return `Uploading ${total} file${total !== 1 ? "s" : ""} — scroll to view all`;
    },
    showFileCountHint() {
      return this.hasFiles && (this.showScrollHint || this.overallProgress === 100);
    },
  },

  mounted() {
    const orderToken = this.meta.token;

    this.uppy = new Uppy({
      id: this.uppyId,
      autoProceed: true,
      allowMultipleUploadBatches: true,
      restrictions: {
        maxNumberOfFiles: this.maxFiles,
        allowedFileTypes: this.fileTypes,
      },
      meta: this.meta,
      onBeforeFileAdded: (currentFile, files) => this.onBeforeFileAdded(currentFile, files),
    })
      .use(Dashboard, {
        inline: true,
        target: "#" + this.uppyDashboardUiId,
        hideUploadButton: false,
        browserBackButtonClose: false,
        showRemoveButtonAfterComplete: true,
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        height: 630,
      })
      .use(AwsS3, this.getAwsS3Options(orderToken));

    this.progressTracker = new UploadProgressTracker();
    if (UPLOAD_CONFIG.ENABLE_AUTO_RETRY_ON_RECONNECT) {
      this.networkMonitor = globalNetworkMonitor;
      this.networkMonitor.onReconnect(() => this.retryFailedUploadsAfterReconnect());
    }

    this.uppy
      .on("upload-success", (file, response) => this.onUploadSuccess(file, response))
      .on("upload-error", (file, error) => this.onUploadError(file, error))
      .on("upload", () => {
        this.progressTracker.start();
        this.$emit("upload", true);
      })
      .on("progress", (progress) => this.updateProgressFromUppy(progress))
      .on("complete", (result) => this.onComplete(result))
      .on("file-removed", (file) => this.onFileRemoved(file));
  },
  
  beforeDestroy() {
    this.isDestroyingComponent = true;

    this.clearRateLimitState();

    if (this.networkMonitor) {
      this.networkMonitor.removeReconnectListener(this.retryFailedUploadsAfterReconnect);
    }

    if (this.progressTracker) {
      this.progressTracker.reset();
    }

    if (this.uppy) {
      this.uppy.close();
    }
  },

  methods: {
    uploadFiles(files) {
      if (!this.uppy) {
        return;
      }

      files.forEach((file) => {
        try {
          this.uppy.addFile({
            source: "file input",
            name: file.name,
            type: file.type,
            data: file,
          });
          // eslint-disable-next-line no-empty
        } catch (err) {}
      });
    },

    onChangeFileInput(event) {
      const files = Array.from(event.target.files);

      this.uploadFiles(files);

      event.target.value = null;
    },

    drop(event) {
      event.preventDefault();

      this.uploadFiles(event.dataTransfer.files);

      event.currentTarget.classList.remove("drop-overlay");
    },

    dragover(event) {
      event.preventDefault();

      if (!event.currentTarget.classList.contains("drop-overlay")) {
        event.currentTarget.classList.add("drop-overlay");
      }
    },

    dragleave(event) {
      event.currentTarget.classList.remove("drop-overlay");
    },
    
    retryFile(fileID) {
      this.uppy.retryUpload(fileID);

      this.failedFiles = this.failedFiles.filter(f => f.id !== fileID);
    },

    retryAllFailed() {
      if (this.failedFiles.length === 0) {
        return;
      }

      const count = this.failedFiles.length;
      
      this.uppy.info(`Retrying ${count} failed upload(s)...`, "info", 3000);

      this.uppy.retryAll();
    },

    retryFailedUploadsAfterReconnect() {
      if (this.failedFiles.length === 0 || !this.uppy) return;

      const count = this.failedFiles.length;

      this.$swal({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Network Restored",
        text: `Auto-retrying ${count} failed upload(s)...`,
        showConfirmButton: false,
        timer: 3000,
      });

      this.uppy.info(`Network reconnected. Auto-retrying ${count} failed upload(s)...`, "info", 3000);

      setTimeout(() => this.uppy.retryAll(), 1000);
    },

    isRateLimitError(error) {
      return (
        error?.source?.status === 429 ||
        error?.message?.includes("429") ||
        error?.message?.toLowerCase().includes("too many requests")
      );
    },

    clearRateLimitCountdown() {
      if (this.rateLimitCountdownIntervalId) {
        clearInterval(this.rateLimitCountdownIntervalId);
        this.rateLimitCountdownIntervalId = null;
      }

      this.rateLimitRemainingSeconds = null;
    },

    clearRateLimitState() {
      Object.values(this.rateLimitRetryTimeouts).forEach((id) => clearTimeout(id));
      this.rateLimitRetryTimeouts = {};

      this.clearRateLimitCountdown();
    },

    scheduleRateLimitRetry(file, error) {
      if (this.rateLimitRetryTimeouts[file.id]) {
        clearTimeout(this.rateLimitRetryTimeouts[file.id]);
        delete this.rateLimitRetryTimeouts[file.id];
      }

      if (this.rateLimitCountdownIntervalId) {
        clearInterval(this.rateLimitCountdownIntervalId);
        this.rateLimitCountdownIntervalId = null;
      }
      
      const retryAfter =
        error?.source?.retryAfter ??
        error?.source?.headers?.["retry-after"] ??
        error?.source?.headers?.["Retry-After"];
      const delayMs = parseRetryAfter(retryAfter);
      const delaySec = Math.ceil(delayMs / 1000);

      this.rateLimitRemainingSeconds = delaySec;
      this.rateLimitCountdownIntervalId = setInterval(() => {
        this.rateLimitRemainingSeconds = Math.max(0, this.rateLimitRemainingSeconds - 1);
        if (this.rateLimitRemainingSeconds <= 0 && this.rateLimitCountdownIntervalId) {
          clearInterval(this.rateLimitCountdownIntervalId);
          this.rateLimitCountdownIntervalId = null;
        }
      }, 1000);

      this.uppy.info(`Upload rate limited. Automatically retrying in ${delaySec}s…`, "info", 3000);

      this.$swal({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Rate limited",
        text: `Retrying in ${delaySec}s...`,
        showConfirmButton: false,
        timer: 3000,
      });

      const timeoutId = setTimeout(() => {
        delete this.rateLimitRetryTimeouts[file.id];
        this.clearRateLimitCountdown();

        if (this.isDestroyingComponent || !this.uppy) return;
        if (this.uppy.getFile(file.id)) {
          this.uppy.retryUpload(file.id);
        }
      }, delayMs);

      this.rateLimitRetryTimeouts[file.id] = timeoutId;
    },

    getChunkSizeForFile(file) {
      const fileSize = file.size;
      let chunkSize = UPLOAD_CONFIG.CHUNK_SIZE;

      if (fileSize > 100 * 1024 * 1024) {
        chunkSize = Math.min(UPLOAD_CONFIG.MAX_CHUNK_SIZE, fileSize / 20);
      }
      chunkSize = Math.max(UPLOAD_CONFIG.MIN_CHUNK_SIZE, Math.min(UPLOAD_CONFIG.MAX_CHUNK_SIZE, chunkSize));

      return Math.floor(chunkSize);
    },

    updateProgressFromUppy(progress) {
      this.overallProgress = Math.round(progress);

      const files = this.uppy.getFiles();
      let totalBytes = 0;
      let uploadedBytes = 0;

      files.forEach((file) => {
        if (file.progress) {
          totalBytes += file.progress.bytesTotal || file.size;
          uploadedBytes += file.progress.bytesUploaded || 0;
        }
      });

      this.progressTracker.update(uploadedBytes, totalBytes);
      this.uploadMetrics.speed = this.progressTracker.getFormattedSpeed();
      this.uploadMetrics.eta = this.progressTracker.getFormattedETA(uploadedBytes, totalBytes);
    },

    onBeforeFileAdded(currentFile, files) {
      const existingInQueue = Object.values(files).find(
        (existingFile) => existingFile?.name === currentFile.name && existingFile?.size === currentFile.size
      );

      if (existingInQueue) {
        this.$swal({
          icon: "warning",
          title: "Duplicate File",
          text: `${currentFile.name} is already in the upload queue.`,
        });
        return false;
      }

      const alreadyUploaded = this.images.find(
        (img) => img.original_name === currentFile.name && img.file_size === currentFile.size
      );

      if (alreadyUploaded) {
        this.$swal({
          icon: "warning",
          title: "Already Uploaded",
          text: `${currentFile.name} has already been uploaded.`,
        });

        return false;
      }

      return true;
    },

    getAwsS3Options(orderToken) {
      return {
        id: "AwsS3",
        limit: UPLOAD_CONFIG.MAX_PARALLEL_FILES,
        retryDelays: UPLOAD_CONFIG.RETRY_DELAYS.slice(0, UPLOAD_CONFIG.MAX_RETRIES),
        shouldUseMultipart: (file) => file.size >= UPLOAD_CONFIG.MULTIPART_THRESHOLD,
        getChunkSize: (file) => this.getChunkSizeForFile(file),
        async getUploadParameters(file) {
          const data = await generatePresignedUrl(file.data, orderToken, "original");
          file.meta.cdnUrl = data.cdn_url;
          file.meta.objectKey = data.object_key;

          return {
            method: "PUT",
            url: data.presigned_url,
            headers: { "Content-Type": file.type || "application/octet-stream" },
          };
        },
        async createMultipartUpload(file) {
          const data = await initiateMultipartUpload(file.data, orderToken, "original");
          file.meta.cdnUrl = data.cdn_url;
          file.meta.objectKey = data.object_key;

          return { uploadId: data.upload_id, key: data.object_key };
        },
        async signPart(file, { uploadId, key, partNumber }) {
          const data = await getPresignedPartUrl(uploadId, key, partNumber, orderToken);
          if (data.already_uploaded && data.etag) {
            return { url: data.presigned_url, headers: {}, expires: 0 };
          }

          return { url: data.presigned_url };
        },
        async completeMultipartUpload(file, { uploadId, key, parts }) {
          const formattedParts = parts.map((p) => ({
            part_number: p.PartNumber,
            etag: p.ETag || p.etag,
          }));

          const data = await completeMultipartUploadService(
            uploadId,
            key,
            formattedParts,
            file.name,
            file.size,
            file.meta.cdnUrl,
            orderToken,
            "original"
          );

          return { location: data.cdn_url };
        },
        async abortMultipartUpload(file, { uploadId, key }) {
          await abortMultipartUpload(uploadId, key, orderToken);
        },
      };
    },

    onUploadSuccess(file, response) {
      const orderToken = this.meta.token;

      if (file.size < UPLOAD_CONFIG.MULTIPART_THRESHOLD) {
        confirmUpload({
          token: orderToken,
          object_key: response.body?.object_key || file.meta.objectKey,
          filename: file.name,
          file_size: file.size,
          cdn_url: response.uploadURL || response.body?.cdn_url,
          image_type: "original",
        })
          .then((imageRecord) => this.$emit("uploaded", file, { body: imageRecord }))
          .catch((err) => {
            console.error("Error confirming upload:", err);
            this.$emit("upload-error", file, err);
          });
      } else {
        this.$emit("uploaded", file, response);
      }

      this.failedFiles = this.failedFiles.filter((f) => f.id !== file.id);
    },

    onUploadError(file, error) {
      if (this.isRateLimitError(error)) {
        this.scheduleRateLimitRetry(file, error);

        return;
      }

      if (!this.failedFiles.find((f) => f.id === file.id)) {
        this.failedFiles.push({ id: file.id, name: file.name, error: error.message });
      }

      this.$swal({
        icon: "error",
        title: "Upload Failed",
        html: `<p><strong>${file.name}</strong> failed to upload.</p><p class="text-muted">${error.message}</p>`,
        showCancelButton: true,
        confirmButtonText: "Retry",
        cancelButtonText: "Remove",
      }).then((result) => {
        if (result.isConfirmed) {
          this.uppy.retryUpload(file.id);
        } else {
          this.uppy.removeFile(file.id);
          this.failedFiles = this.failedFiles.filter((f) => f.id !== file.id);
        }
      });
    },

    onComplete(result) {
      this.progressTracker.reset();

      const { successful, failed } = result;

      if (failed.length === 0) {
        if (this.overallProgress === 100) {
          this.clearRateLimitState();

          this.$swal({
            icon: "success",
            title: "Upload Complete!",
            text: `Successfully uploaded ${successful.length} file(s).`,
          });
        } else {
          this.uppy.info(
            `Successfully uploaded ${successful.length} file(s). More remaining...`,
            "info",
            3000
          );
        }
      } else {
        this.$swal({
          icon: "warning",
          title: "Some Uploads Failed",
          html: `<p><strong>${successful.length}</strong> file(s) uploaded.</p><p><strong>${failed.length}</strong> file(s) failed.</p><p>You can retry failed uploads.</p>`,
        });
      }

      this.$emit("complete", result);
    },

    onFileRemoved(file) {
      if (!this.isDestroyingComponent) this.$emit("removed", file);

      this.failedFiles = this.failedFiles.filter((f) => f.id !== file.id);
    },
  },
};
</script>

<style lang="css">
@import "https://releases.transloadit.com/uppy/v3.1.1/uppy.min.css";

.cursor-pointer {
  cursor: pointer;
}
.drop-target .drop-target-add-files {
  border: 2px dashed #dfdfdf;
}
.drop-overlay {
  background-color: #e5e7eb !important;
  border: 3px dashed #6b7280;
}

.uppy-Root,
.uppy-Dashboard,
/* .uppy-Dashboard-inner, */
.uppy-Dashboard-innerWrap,
.uppy-Dashboard-files {
  height: 100% !important;
  width: 100% !important;
}

.uppy-Dashboard-files {
  overflow-y: auto !important;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.uppy-DashboardContent-bar {
  display: none !important;
}

.uppy-size--md .uppy-Dashboard-inner {
  min-height: auto;
  min-width: 100%;
}

.uppy-Dashboard-AddFiles-list,
.uppy-StatusBar-actions .uppy-StatusBar-actionBtn--done {
  display: none !important;
}

[dir="ltr"] .uppy-size--md .uppy-Dashboard-Item-action--remove {
  right: -6px !important;
  top: -9px !important;
}

.uppy-StatusBar,
.uppy-Dashboard-Item-previewInnerWrap:after,
.uppy-DashboardContent-bar,
.uppy-size--md .uppy-Dashboard-Item-action--remove,
.uppy-Dashboard-Item-progress {
  z-index: 50 !important;
}

.upload-metrics {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
  font-size: 0.9rem;
}

.rate-limit-countdown {
  background-color: #faf6ec;
  border: 1px solid #e38800;
  font-size: 0.9rem;
}

.rate-limit-countdown span {
  color: #e38800;
  font-weight: 500;
}

.failed-files-actions {
  display: flex;
  justify-content: flex-end;
}

.uppy-Dashboard-Item-preview {
  height: auto !important;
}

.uppy-Dashboard-Item.is-complete.is-resumable,
.uppy-Dashboard-Item.is-inprogress,
.uppy-Dashboard-Item-previewImg, 
.uppy-Dashboard-Item-preview {
  max-width: 95px !important;
  max-height: 160px !important;
}

.uppy-Dashboard-Item-fileInfoAndButtons {
  padding-top: 5px !important;
}

.uppy-Dashboard-Item-name {
  font-size: 11px !important;
}

.uppy-Dashboard-Item-status {
  font-size: 10px !important;
}

.uppy-Dashboard-Item-previewInnerWrap {
  border-radius: 10px !important;
}
</style>
